import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Ban, Lock, ArrowRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

interface VoidModalProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  wo: any;
  woLines: any[];
  onComplete: () => void;
}

export const WOVoidModal = ({ open, onOpenChange, wo, woLines, onComplete }: VoidModalProps) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const linesForWo = woLines.filter((wol: any) => wol.work_order_id === wo.id && wol.status !== "voided");

  const handleVoid = async () => {
    if (!reason.trim()) return toast.error("Reason is required");
    setBusy(true);
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const { data, error } = await supabase.rpc("void_work_order", {
        p_wo_id: wo.id,
        p_reason: reason.trim(),
        p_user_id: userId,
      });
      if (error) throw error;
      const result = data as any;
      if (!result.success) {
        toast.error(result.message);
        return;
      }
      toast.success(`${wo.number} Voided`, {
        description: `${result.lines_released} units returned to available pool.`,
      });
      onOpenChange(false);
      onComplete();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Ban className="h-5 w-5 text-destructive" />
            Void Work Order — {wo.number}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-start gap-2 p-3 rounded-md border border-destructive/30 bg-destructive/5 text-sm">
            <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-destructive">This will permanently void this work order.</p>
              <ul className="mt-2 space-y-1 text-muted-foreground list-disc ml-4">
                <li>All {linesForWo.length} assigned units will be unassigned</li>
                <li>Units return to the available pool immediately</li>
                <li>{wo.number} will be marked voided in audit log</li>
                <li>This cannot be undone</li>
              </ul>
            </div>
          </div>

          {linesForWo.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Units that will be released:</p>
              <div className="max-h-40 overflow-y-auto border rounded-md p-2 space-y-0.5">
                {linesForWo.map((wol: any) => (
                  <div key={wol.id} className="flex justify-between text-xs font-mono">
                    <span>{wol.unit_schedule?.unit_number ?? "—"} {wol.description}</span>
                    <Badge variant="outline" className="text-[9px]">{wol.status}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">Reason (required)</label>
            <Textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Why is this WO being voided?"
              rows={3}
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={busy || !reason.trim()}
              onClick={handleVoid}
            >
              {busy ? "Voiding…" : "Void Work Order →"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

interface CloseModalProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  wo: any;
  woLines: any[];
  onComplete: () => void;
  onBillFirst?: () => void;
}

export const WOCloseModal = ({ open, onOpenChange, wo, woLines, onComplete, onBillFirst }: CloseModalProps) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const linesForWo = woLines.filter((wol: any) => wol.work_order_id === wo.id);
  const unbilledLines = linesForWo.filter((wol: any) => wol.status === "pending" || wol.status === "complete");
  const billedLines = linesForWo.filter((wol: any) => wol.status === "billed" || wol.status === "paid");
  const retainageHeld = linesForWo.reduce((s: number, wol: any) => s + Number(wol.retainage_held ?? 0), 0);

  const handleClose = async () => {
    setBusy(true);
    try {
      const userId = (await supabase.auth.getUser()).data.user?.id;
      const { data, error } = await supabase.rpc("close_work_order", {
        p_wo_id: wo.id,
        p_reason: reason.trim() || "Closed by controller",
        p_user_id: userId,
      });
      if (error) throw error;
      const result = data as any;
      if (!result.success) {
        toast.error(result.message);
        return;
      }
      toast.success(`${wo.number} Closed`, {
        description: `${result.lines_released} units returned to pool. ${result.lines_locked} billed lines locked.${retainageHeld > 0 ? ` ${fmt(retainageHeld)} retainage still held.` : ""}`,
      });
      onOpenChange(false);
      onComplete();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-muted-foreground" />
            Close Work Order — {wo.number}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Closing will lock billed lines and release any unbilled lines back to the SOV pool.
          </p>

          <div className="grid grid-cols-2 gap-3">
            <div className="border rounded-md p-3 text-center">
              <p className="text-2xl font-bold">{billedLines.length}</p>
              <p className="text-xs text-muted-foreground">Billed lines (stay locked)</p>
            </div>
            <div className="border rounded-md p-3 text-center">
              <p className="text-2xl font-bold">{unbilledLines.length}</p>
              <p className="text-xs text-muted-foreground">Unbilled lines (returned)</p>
            </div>
          </div>

          {unbilledLines.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-muted-foreground">Lines being returned to pool:</p>
              <div className="max-h-40 overflow-y-auto border rounded-md p-2 space-y-0.5">
                {unbilledLines.map((wol: any) => (
                  <div key={wol.id} className="flex justify-between text-xs font-mono">
                    <span>{wol.unit_schedule?.unit_number ?? "—"} {wol.description}</span>
                    <Badge variant="outline" className="text-[9px]">{wol.status}</Badge>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                These units will be immediately available for assignment to other work orders.
              </p>
            </div>
          )}

          {retainageHeld > 0 && (
            <div className="flex items-center gap-2 text-sm p-2 rounded-md bg-muted/50">
              <span className="text-muted-foreground">Retainage held:</span>
              <span className="font-semibold">{fmt(retainageHeld)}</span>
              <span className="text-xs text-muted-foreground">(release separately)</span>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-medium">Reason</label>
            <Textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Optional close reason"
              rows={2}
            />
          </div>

          <div className="flex justify-between gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <div className="flex gap-2">
              {onBillFirst && unbilledLines.some((l: any) => l.status === "complete") && (
                <Button variant="outline" onClick={() => { onOpenChange(false); onBillFirst(); }}>
                  <ArrowRight className="h-3.5 w-3.5 mr-1" />
                  Bill remaining first
                </Button>
              )}
              <Button
                disabled={busy}
                onClick={handleClose}
              >
                {busy ? "Closing…" : "Close and Release Units →"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
